import Checkbox from '@mui/material/Checkbox';
import React from 'react';
import * as yup from 'yup';
import consts from '../consts';
import Model from '../models/model';
import Connection from '../services/Connection';
import util from '../util';
import CatAdminForm from '../views/forms/CatAdminForm';
import { useGridBase } from '../components/grid/GridBaseContext';
import { dataStore } from '../Store';
import { syncFieldTests } from './UserSyncTable';
import { itemLogColumns } from './EndoSetupSopes';

const { nonHuluScopes } = consts;

export const { unitArrayConverter, canPrint, isGridReadOnly, canAdd, viewOnly, deviceTypeRenderr, isFromTrackingSystem, isVisible, isValidFile } = util;
const scopesMapper = {
	"HuluScopes" : ['L', 'INNOV', 'I', 'RAER', 'C', 'RC', 'ELC', 'ADV', 'ADVPT', 'EDRY', 'ISA', 'DH'],
	"NonHuluScopes" : nonHuluScopes,
	"HookupScopes": ['RAER']
}
/**
 * @dsec UUId is not handel ad backend so its commited out once its handeled so uncomment it
 * And other field are comment becuase the array of obj funcationailty is not written once its written uncomment it 
 */


const onNewHideColumn = ({ formik }) => {
	return Boolean(formik?.values?.Id);

}

const CustomCheckBox = ({ row }) => {
	const { rowSelectionModel, onRowSelectionModel } = useGridBase();
	const handleOnClick = () => {
		const result = Array.from(rowSelectionModel.ids).find(item => item === row._id);
		if(result)
		{
			const updatedRowSelectionModel = Array.from(rowSelectionModel.ids).filter(item => item !== row._id);
			rowSelectionModel.ids = new Set(updatedRowSelectionModel);
			onRowSelectionModel(rowSelectionModel);
		}
		else{
			rowSelectionModel.ids.add(row._id);
			onRowSelectionModel(rowSelectionModel);
		}
	}

	const handleChecked = () => {
		return rowSelectionModel.ids.has(row._id);
	}
	return (
		<Checkbox
			checked={handleChecked()}
			onClick={handleOnClick}
			disabled={viewOnly()}
			color="primary"
			value={row?.id}
			inputProps={{ 'aria-label': 'checkbox' }}
		/>
	)
}

const fetchDeviceType = async ({ activeRecord }) => {
	try {
		const { record = {} } = activeRecord || {}
		const { id = null, Parameters = [] } = record;
		let inValue = '';
		const parameterMapper = new Map(Parameters.map((parameter) => [parameter.Key, parameter.Value]));
		const isHuluScope = parameterMapper.get('HuluScopeModelId');
		const isHookupScope = parameterMapper.get("Hookup Block");
		if (isHuluScope && !!id) {
			inValue = scopesMapper["HuluScopes"];
		} else {
			inValue = isHookupScope ? scopesMapper["HookupScopes"] : scopesMapper["NonHuluScopes"];
		}
		const where = { CustomStringValue: { $exists: true, $in: inValue } }
		const formData = new FormData();
		formData.append('comboType', 'DeviceType');
		formData.append('CustomeWhere', JSON.stringify(where))
		const { options, count } = await fetch(Connection.Combo, {
			method: 'POST',
			body: formData
		}).then((res) => res.json()).then((json) => {
			const { records } = json;
			let options = records?.map((record) => ({
				id: record.LookupId,
				label: record.DisplayValue
			}));
			if (!Array.isArray(options)) {
				options = [];
			}
			return { options: options || [], count: options?.length };
		});
		return { options, count };
	} catch (error) {
		throw new Error("Error fetching deviceTypes:", error);
	}
}

const disabledScope = ({ activeRecord }) => {
	if (activeRecord.id && activeRecord.record?.ItemType === 'Scope') {
		return true;
	}
	return false;
}

// Updates the Formik form state with the selected department value.
const onChangeDepartment = ({ formik, value }) => {
	formik.setValues({ ...formik.values, Department: value });
}

// Returns the label property from the given value object for display in autocomplete.
const autocompleteValueFormatter = (value = {}) => {
	return (value && value.label) || '';
}

/**
 * Sends a request to create a new department option by submitting form data
 * with predefined action and ID, and values from the provided option.
 *
 * @function onCreateOption
 * @param {Object} value - The object containing the new option to create.
 * @param {{ option: { id: string | number, label: string } }} value.option - The option data to save.
 */
const onCreateOption = async (value = {}) => {
	const { id, label } = value.option || {};
	if (consts.regex.mongoId.test(id)) {
		return;
	}
	const formData = new FormData();
	formData.append('action', 'save');
	formData.append('id', 0);
	formData.append('CustomStringValue', id);
	formData.append('DisplayValue', label);
	try {
		await fetch(Connection.DepartmentLookup, {
			method: 'POST',
			body: formData,
			credentials: 'include'
		})
	} catch (error) {
		throw new Error("Error creating record:", error);
	}
}

/**
 * Fetches the list of departments from the server using a POST request
 * with the `action=list` parameter. It processes the response to generate
 * an array of department options in `{ id, label }` format.
 *
 * @async
 * @function fetchDepartment
 * @returns {Promise<{ options: Array<{ id: string | number, label: string }>, count: number }>} 
 *          A promise that resolves to an object containing:
 *          - `options`: Array of department options with `id` and `label`
 *          - `count`: Number of options retrieved
*/
const fetchDepartment = async ({ activeRecord }) => {
	try {
		const formData = new FormData();
		const { auth } = dataStore.getState();
		formData.append('comboType', 'DepartmentLookup');
		formData.append('clientId', auth.tags.ClientId);

		const response = await fetch(Connection.Combo, { method: 'POST', body: formData });
		const json = await response.json();
		const records = json.records || [];
		const options = records.map((record) => ({
			id: record.LookupId,
			label: record.DisplayValue,
		}));

		return { options, count: options.length };
	} catch (error) {
		throw new Error("Error fetching departments:", error);
	}
}

const columnModel = {
	name: 'ItemSync',
	gridTitle:'ItemSync',
	idProperty: "_id",
	disableAdd: canAdd,
	readOnly: isGridReadOnly,
	isRevisionLogBtn: true, // adding this Property to Get Revision Log button on Form Screen.
	allowDelete: false,
	disableDelete: true,
	disableExport: false,
	disablePrintBarcode: false,
	disableSelectAll: false,
	defaultSort: 'ItemName ASC',
	showGridRefreshBtn: true,
	allowImport: util.isAllowImport(),
	importProps: { message: "ScopesCountWarning", maxCount: consts.maxScopesCount, logColumns: itemLogColumns, fileType: consts.endoImportSpec.Scopes.fileType, type: consts.endoImportSpec.Scopes.type },
	apiParams: ({ searchParams }) => ({ type: searchParams.get('type') || 'scopes', value: searchParams.get('value') || '' }),
	customizeSaveData: (toSave, { values, activeRecord = {} }) => {
		const { HookupValue } = activeRecord.record;
		toSave.RemoveCodes = JSON.stringify(values?.RemoveCodes || []);
		toSave.Parameters = JSON.stringify(values?.Parameters || []);
		toSave.ItemCodes = JSON.stringify(values?.ItemCodes || []);
		toSave.DeviceTypes = JSON.stringify(values?.DeviceTypes || []);
		toSave.HookupValue = HookupValue || '';
		return toSave;
	},
	FormExtension: ({ formik, activeRecord, isNew, modelConfig }) => {
		return (
			<CatAdminForm activeRecord={activeRecord} formik={formik} isDeviceType={false} modelConfig={modelConfig}
				isNew={isNew}
				isItemSync={true}
			/>
		)
	},
	onFormLoad: ({ activeRecord, formik }) => {
		const { record } = activeRecord;
		if(activeRecord.id) {
			return;
		}
		formik.setFieldValue('ItemType', record.ItemType);
		formik.setFieldValue('DeviceTypes', record.DeviceTypes);
		formik.setFieldValue('EquipmentModel', record.EquipmentModel);
		formik.setFieldValue('Parameters', record.Parameters);
		formik.setFieldValue('Department', record.Department);
	},
	formGroups: [{
		fields: ["ItemName", "Manufacturer", "ItemType", "Model", "SerialNumber", "IsActive", "ReSync",
			"LastResyncOn", "InterFacedYN", "DataSource", "EquipmentName", "DeviceTypes", "ItemCodes", "EquipmentModel", "Department"
		]
	}],
	columns: [
		{ field: 'ItemUID', width: 150, hidden: true, readOnly: true, inputWhen: onNewHideColumn },
		{ field: 'ItemName', width: 150, required: true, readOnly: ({ formik }) => viewOnly(false, formik), inputProps: { autoFocus: false } },
		{ header: 'ItemType', field: 'ItemType', width: 150, readOnly: true, disabled: disabledScope },
		{ header: 'SerialNumberSecond', field: 'SerialNumber', width: 150, readOnly: isFromTrackingSystem },
		{ field: 'Department', header: 'Department', width: 120, lookup: fetchDepartment, autocompleteValueFormatter, filterMode: "server", freeSolo: true, onChange:onChangeDepartment, onCreateOption, inputWhen: util.isDepartmentVisible, hidden: true },
		{ header: 'Active', field: 'IsActive', type: 'bool', width: 200, readOnly: ({ formik }) => viewOnly(false, formik), defaultValue: true },
		{ header: 'BarcodeCount', field: 'CodesCount', width: 150, isNotformfield: true, hidden: true },
		{ field: 'DataSource', width: 150, inputWhen: onNewHideColumn, readOnly: true, hidden: true },
		{ header: 'DefaultBarcode', field: 'DefaultBarcode', width: 200 },
		{ header: 'DefaultRFID', field: 'DefaultRFID', hidden: true, width: 200 },
		{ field: 'CreatedOn', width: 200, type: 'date', readOnly: true, isLocal: true, format: 'MM/DD/YYYY hh:mm:ss A', inputWhen: onNewHideColumn, isNotformfield: true },
		{ header: 'updatedAt', width: 200, field: 'ModifiedOn', type: 'date', isLocal: true, format: 'MM/DD/YYYY hh:mm:ss A', readOnly: true, inputWhen: onNewHideColumn },
		{
			field: 'ItemCodes', width: 150, hidden: true, type: 'object', validate: yup.array().of(
				yup.object().shape({
					CodeType: yup.string().required(),
					Code: yup.string(),
					IsActive: yup.boolean(),
					DefaultValue: yup.boolean(),
					RFIDTagNumber: yup.string(),
					RFIDEquipmentID: yup.string()
				})
			)
				.test(syncFieldTests.DuplicateBarcodeFound)
				.test(syncFieldTests.AtLeastOneBarcodeisRequired)
				.test(syncFieldTests.AtLeastOneDefaultBardcodeisRequired)
				.test(syncFieldTests.OverDefaultBarcodeFound)
				.test(syncFieldTests.CodeisRequired)
				.test(syncFieldTests.CodeLengthMax100)
				.test(syncFieldTests.DuplicateISARFIDFound)
				.test(syncFieldTests.DuplicateRapidRFIDTagFound)
				.test(syncFieldTests.ValidateRFIDTagNumberHex)
				.test(syncFieldTests.validateRFIDTagNumberFormat({
					prefix: "2C",
					tagIdStartSegment: 2,
					tagIdEndSegment: 12,
					totalRfidLength: 14
				})),
			excludeOnGrid: true,
			isNotExportField: true,
			isNotformfield: true
		},
		{
			header: 'Parameters', field: 'Parameters', width: 150, lookup: 'ItemSyncParameters', hidden: true, type: 'object', validate: yup.array().of(
				yup.object().shape({
					Key: yup.string().required(),
					Value: yup.string().required()
				})
			),
			excludeOnGrid: true,
		},
		{ field: 'ReSync', type: 'bool', hidden: true, width: 200, inputWhen: onNewHideColumn, readOnly: ({ formik }) => viewOnly(false, formik), defaultValue: true },
		{ field: 'LastResyncOn', header: 'LastResyncOn', type: 'date', format: 'MM/DD/YYYY hh:mm:ss A', hidden: true, width: 200, readOnly: true, inputWhen: onNewHideColumn, isLocal: true },
		{ field: 'ModifiedBy', hidden: true, width: 200, readOnly: true, inputWhen: onNewHideColumn },
		{ header: 'PrintBarcode', field: 'Print', type: 'action', width: 120, renderCell: CustomCheckBox, isNotExportField: true },
		{ header: 'EquipmentModel', field: 'EquipmentModel', width: 120, lookup: 'Model', multiple: true, readOnly: true, excludeOnGrid: true },
		{ field: 'DeviceTypes', header: 'DeviceTypes', width: 120, defaultValue: ['RC', 'EDRY', 'ISA'], hidden: true, lookup: fetchDeviceType, multiple: true, type: 'object', valueGetter: deviceTypeRenderr, readOnly: isFromTrackingSystem, inputWhen: isVisible },
	],
	extraExportConfig: {
		PDF: {
			pageSize: 'A2'
		}
	}
};

export const itemSyncConfig = columnModel;
export const ItemSyncModel = new Model(columnModel);
export default ItemSyncModel.createFormBase();